import React, { useEffect, useState, useRef, useMemo } from "react";
import { useHashAnchor } from '../../hooks/useHashAnchor'
import { get } from '../../utils/request'
import "./index.less";
import { adaptSize } from '../../utils/remAdapter'
import RecruitmentModal from './components/RecruitmentModal';

import { gsap } from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Pagination from '../../components/Pagination';

// 第一屏图片
import mouse from '../../assets/newVersion/mouse.svg'
import zhadian from '../../assets/newVersion/contact/zhadian.png'
import dalogo from '../../assets/newVersion/contact/dalogo.png'
import sharedFuture from '../../assets/newVersion/shared-future.svg'

// 第二屏图片
import location from '../../assets/newVersion/contact/location.svg'
import email from '../../assets/newVersion/contact/email.svg'
import phone from '../../assets/newVersion/contact/phone.svg'

gsap.registerPlugin(ScrollTrigger)

const Contact: React.FC = () => {
  useHashAnchor();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);

  // 🎯 修复点1：将 useRef 改为 useState，这样数据更新才会触发渲染
  const [jobList, setJobList] = useState<any[]>([]);

  // 控制弹窗显示状态
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selJob, setSelJob] = useState('');

  // 打开弹窗
  const handleOpenModal = (job: string) => {
    setSelJob(job);
    setIsModalOpen(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // 🎯 修复点2：获取数据后使用 setState 更新
  async function getList() {
    try {
      const res = await get('https://dafangyuntu.com/tool/getJobList')
      console.log('res:', res)
      // 使用 setState 替代直接修改 ref，这会触发重新渲染
      setJobList(res.data || [])
    } catch (error) {
      console.log('error:', error)
      setJobList([]) // 错误时设为空数组，避免 undefined
    }
  }

  useEffect(() => {
    if (!hasFetched.current) {
      getList();
      hasFetched.current = true;
    }
    // 滚动到顶部 - 使用现代的 scrollTo API
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    const ctx = gsap.context(() => {
      // 首屏初始状态
      const firstScreen = document.querySelector('.scroll-first');
      const targetWidth = adaptSize(508) as number;
      const targetHeight = adaptSize(286) as number;
      const scaleX = targetWidth / window.innerWidth;
      const scaleY = targetHeight / window.innerHeight;

      // 取较小值保持等比缩放
      const targetScale = Math.min(scaleX, scaleY);

      // 首屏缩小+上移动画（与二屏内容入场衔接）
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.pin-wrapper', // 使用包裹层作为触发器
          start: 'top top',
          end: '+=100%',
          pin: true,
          scrub: 1,
        }
      });

      // 阶段1：首屏缩小并向上移动（0-50%进度）
      tl.to('.scroll-first', {
        scale: targetScale,
        y: '-50vh', // 向上移动一半视口高度（可根据需要调整至完全移出）
        opacity: 1, // 可选：逐渐消失
        duration: 0.5,
        ease: 'none'
      }).to('.mouse', {
        opacity: 0,
        duration: .2
      }, '<');

      // 阶段2：二屏内容依次入场（30-80%进度，与首屏离开重叠）
      tl.to('.scroll-second .s-left', {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'none'
      }, 0.3);

      tl.to('.scroll-second .zhadian', {
        opacity: 1,
        // scale: 1,
        duration: 0.3,
        ease: 'none'
      }, 0.3);

      tl.to('.scroll-second .dalogo', {
        opacity: 1,
        // scale: 1,
        duration: 0.2,
        ease: 'none'
      }, 0.6);

      // 三屏动画初始化（保持原有逻辑）
      gsap.set('.scroll-third .title-box', { opacity: 0, y: adaptSize(30) });
      gsap.set('.scroll-third .l-item', { opacity: 0, y: adaptSize(30) });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  // 三屏入场动画
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set('.scroll-third .title-box', { opacity: 0, y: adaptSize(30) });
      gsap.set('.scroll-third .l-item', { opacity: 0, y: adaptSize(30) });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.scroll-third',
          start: 'top 75%',
          toggleActions: 'play none none none',
        }
      });

      tl.to('.scroll-third .title-box', {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      })
        .to('.scroll-third .l-item:nth-child(1)', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out'
        }, '-=0.3')
        .to('.scroll-third .l-item:nth-child(2)', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out'
        }, '-=0.3')
        .to('.scroll-third .l-item:nth-child(3)', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out'
        }, '-=0.3')
        .to('.scroll-third .l-item:nth-child(4)', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out'
        }, '-=0.3');

    }, containerRef);

    return () => ctx.revert();
  }, [currentPage, jobList]); // 🎯 修复点3：依赖项添加 jobList，确保数据获取后触发动画

  // 🎯 修复点4：使用 useMemo 优化 columns 计算，避免每次渲染重复计算
  const columns = useMemo(() => {
    const itemsPerPage = 12;
    const startIdx = (currentPage - 1) * itemsPerPage;
    const currentItems = jobList.slice(startIdx, startIdx + itemsPerPage);

    const cols: any[][] = [[], [], [], []];
    currentItems.forEach((item: any, index: number) => {
      cols[index % 4].push(item);
    });
    return cols;
  }, [jobList, currentPage]);

  // 计算总页数
  const totalPages = Math.ceil(jobList.length / 12) || 1;

  return (
    <div className="contact-page" ref={containerRef}>
      {/* 首屏+二屏包裹层，用于固定滚动 */}
      <div className="pin-wrapper">
        {/* 第一屏：全屏背景 */}
        <div className="scroll-first">
          <div className="f-title">携手共赢·美好未来</div>
          <img className="f-msg" src={sharedFuture} />
          <img src={mouse} alt="" className="mouse" />
        </div>

        {/* 第二屏：绝对定位在首屏下方，无间隙 */}
        <div className="scroll-second" id="lianxiwomen">
          <div className="ss-box">
            <div className="s-left">
              <div className="square"></div>
              <div className="sl-title">联系我们</div>

              <div className="msg-item">
                <img src={location} alt="" className="left" />
                <div className="right">
                  <div className="sr-msg">地址</div>
                  <div className="sr-title">北京市朝阳区创远路26号院1号楼4层402</div>
                </div>
              </div>
              <div className="msg-item">
                <img src={email} alt="" className="left" />
                <div className="right">
                  <div className="sr-msg">邮箱</div>
                  {/* <div className="border"></div> Hr@dafang-inc.cloud */}
                  <div className="sr-title">Dfpublic@dafangyuntu.com</div>
                </div>
              </div>
              <div className="msg-item">
                <img src={phone} alt="" className="left" />
                <div className="right">
                  <div className="sr-msg">电话</div>
                  <div className="sr-title">131 2116 2261</div>
                </div>
              </div>
            </div>
            <div className="s-right">
              <img src={zhadian} alt="" className="zhadian" />
              <img src={dalogo} alt="" className="dalogo" />
            </div>
          </div>
        </div>
      </div>

      {/* 第三屏：正常流，紧随包裹层之后 */}
      <div className="scroll-third" id="jiaruwomen">
        <div className="title-box">
          <div className="title">加入我们</div>
          <div className="msg">JOIN US 精英团队，期待你的加入！</div>
        </div>

        <div className="list">
          {columns.map((col, colIndex) => (
            <div className="l-item" key={colIndex}>
              {col.map((job: any, idx: number) => (
                <div className="li-item" key={idx}>
                  <div className={`
                      ${job.tag == '急招' ? 'square-red' : ''} 
                      square
                    `}></div>
                  <div className="title">
                    {job.title}
                    {job.tag && <div className="red-tag">{job.tag}</div>}
                  </div>
                  <div className="basic">
                    类型：{job.type}｜招聘人数：{job.num}｜学历：{job.edu}<br />
                    工作年限：{job.years}｜工作地点：{job.location}
                  </div>
                  <div className="others">
                    <div className="title">岗位职责</div>
                    <ol className="msg">
                      {job.require.map((item: string) => (<li>{item}</li>))}
                    </ol>
                  </div>
                  <div className="others">
                    <div className="title">任职要求</div>
                    <ol className="msg">
                      {job.duties.map((item: string) => (<li>{item}</li>))}
                    </ol>
                  </div>

                  <div className="apply-btn"
                    onClick={() => { handleOpenModal(job.title) }}>投递简历</div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(e: number) => setCurrentPage(e)}
        />

        {/* 弹窗组件 - 通过 props 控制显示 */}
        <RecruitmentModal
          position={selJob}
          open={isModalOpen}
          onOpenChange={handleCloseModal}
        />
      </div>
    </div>
  );
};

export default Contact;